import { defineStore } from 'pinia';
import {
  computed,
  del,
  reactive,
  ref,
  set,
} from 'vue';

import type {
  AllGlobalFlatsFilters,
  CollectionItem,
} from '@/entities/collection/api';
import {
  addedCollectionComment,
  addToCollection,
  checkIsInCollection,
  editedCollectionComment,
  getAvailableCollectionItems,
  getUnavailableCollectionItems,
  loadFacets,
  loadFlatsForCollection,
  loadSpecs,
  removeFromCollection,
} from '@/entities/collection/api';
import { getWrappedToArray } from '@/shared/filterSystem/Filter';
import type { LabelValueDisabled } from '@/shared/filterSystem/typeUtils';
import {
  getDecodedId,
  getEncodedId,
} from '@/shared/lib';
import type { PageInfoGraphql } from '@/types/PageInfo';
import type {
  LabelValue,
  Range,
} from '@/types/Type';

import type { CollectionRawLot } from './types';
import type { SpecsRange } from '@strana-artw/realty';

export interface CollectionLot extends CollectionRawLot {
  comment: string;
}

// TODO: Техдолг
export function rewrapGlobalId (source: string) {
  // TODO см. места, где используется rewrapGlobalId - надо заменить использование разных globalId (с разными префиксами) на profitbase_id (pk)
  return getEncodedId(`GlobalFlatType:${ getDecodedId(source) }`);
}
// TODO: Техдолг
export function rewrapPkToGlobalId (source: number) {
  // TODO см. места, где используется rewrapPkToGlobalId - надо заменить использование разных globalId (с разными префиксами) на profitbase_id (pk)
  return getEncodedId(`GlobalFlatType:${ source }`);
}

export const useCollectionStore = defineStore('collection', () => {
  const loading = ref<boolean>(false);

  async function isInCollection<
    TArrayOrSingle extends string | string[],
    TResult = TArrayOrSingle extends string[] ? Record<string, boolean> : boolean,
  > (globalId: TArrayOrSingle): Promise<TResult> {
    const response = await checkIsInCollection(Array.isArray(globalId) ? globalId.map(rewrapGlobalId) : rewrapGlobalId(globalId));
    if (Array.isArray(globalId)) {
      return response.data.data.reduce((acc, item) => {
        acc[item.id] = item.is_collections;
        return acc;
      }, {} as Record<string, boolean>) as TResult;
    } else {
      return (response.data.data?.at(0)?.is_collections ?? false) as TResult;
    }
  }

  function addItem (globalId: string) {
    return addToCollection(rewrapGlobalId(globalId)).then((response) => response.data.is_collections);
  }

  function removeItem (globalId: string) {
    return removeFromCollection(rewrapGlobalId(globalId)).then((response) => response.data.is_collections);
  }

  interface SpecsType {
    project: Array<LabelValueDisabled>;
    rooms: Array<LabelValueDisabled>;
    cost: Range | undefined;
    square: Range | undefined;
  }

  const loadedSpecs = reactive<SpecsType>({
    project: [],
    rooms: [],
    cost: undefined,
    square: undefined,
  });

  interface FacetsType {
    project: Array<string>;
    rooms: Array<string>;
    cost: Range | undefined;
    square: Range | undefined;
  }

  const facets = reactive<FacetsType>({
    project: [],
    rooms: [],
    cost: undefined,
    square: undefined,
  });

  function ensureRange (specsRange: SpecsRange | undefined): Range | undefined {
    return specsRange?.min == null || specsRange.max == null ? undefined : (specsRange as Range);
  }

  function mergeRanges (specsRange: SpecsRange | undefined, facetsRange: SpecsRange | undefined): Range | undefined {
    const min = facetsRange?.min || specsRange?.min;
    const max = facetsRange?.max || specsRange?.max;
    return min == null || max == null ? undefined : { min, max };
  }

  async function getSpecs (ids: string[]) {
    const result = await loadSpecs(ids);
    loadedSpecs.cost = ensureRange(result.specs.fullFinalPrice?.range);
    loadedSpecs.square = ensureRange(result.specs.area?.range);
    loadedSpecs.project = (result.specs.project?.choices?.map((choice) => choice.categories ?? []).flat() ?? []) as LabelValue[];
    loadedSpecs.rooms = result.specs.rooms?.choices ?? [];
  }

  async function getFacets (ids: string[]) {
    const result = await loadFacets(ids);

    facets.cost = ensureRange(result.facets.fullFinalPrice?.range);
    facets.square = ensureRange(result.facets.area?.range);
    facets.project = result.facets.project?.choices?.map((choice) => choice.categories ?? []).flat()
      .map((item) => item.value) ?? [];
    facets.rooms = result.facets.rooms?.choices?.map((item) => String(item.value)) ?? [];
  }

  const specs = computed<SpecsType>(() => ({
    project: loadedSpecs.project.map((item) => facets.project.includes(item.value) ? item : { ...item, disabled: true }),
    rooms: loadedSpecs.rooms.map((item) => facets.rooms.includes(item.value) ? item : { ...item, disabled: true }),
    cost: mergeRanges(loadedSpecs.cost, facets.cost),
    square: mergeRanges(loadedSpecs.square, facets.square),
  }));

  const availableFlatsIds = ref<Record<string, string>>({});
  const unavailableFlatsIds = ref<Record<string, string>>({});
  const flatsInfo = computed<Record<string, string>>(() => ({ ...availableFlatsIds.value, ...unavailableFlatsIds.value }));
  const totalAvailableCount = ref(0);

  const prepareInfo = (source: CollectionItem[]) => source.reduce((acc, item) => {
    set(acc, getDecodedId(item.Global_id), item.comment);
    return acc;
  }, {} as Record<string, string>);

  function loadCollectionItemsInfo () {
    return Promise.all([
      getAvailableCollectionItems().then((response) => {
        availableFlatsIds.value = prepareInfo(response.data.result);
        totalAvailableCount.value = response.data.count;
      }),
      getUnavailableCollectionItems().then((response) => {
        unavailableFlatsIds.value = prepareInfo(response.data.result);
      }),
    ]);
  }

  const currentAvailable = ref<CollectionLot[]>([]);
  const currentUnavailable = ref<CollectionLot[]>([]);
  const availablePageInfo = ref<PageInfoGraphql | null>(null);
  const unavailablePageInfo = ref<PageInfoGraphql | null>(null);

  function removeItemByPk (pk: number | number[]) {
    const pks = getWrappedToArray(pk);
    const globalIds = pks.map((pk) => rewrapPkToGlobalId(pk));
    return removeFromCollection(globalIds).then((response) => {
      for (const pk of pks) {
        del(availableFlatsIds.value, pk);
        del(unavailableFlatsIds.value, pk);
      }
      return response.data.is_collections;
    });
  }
  interface GetCurrentByIdsArgs {
    available: boolean;
    pageSize?: number;
    append?: boolean;
  }

  async function getCurrentByIds (ids: string[], filters: AllGlobalFlatsFilters, args: GetCurrentByIdsArgs) {
    const [container, pageInfo] = args.available ? [currentAvailable, availablePageInfo] : [currentUnavailable, unavailablePageInfo];

    if (!ids.length) {
      container.value = [];
      return;
    }

    const payload = {
      ids,
      cursor: args.append ? (pageInfo.value?.endCursor ?? '') : '',
      filters,
      pageSize: args.pageSize,
    };

    const result = await loadFlatsForCollection(payload.ids, payload.cursor, payload.filters, payload.pageSize);
    const newItems = result.edges.map((item) => reactive({ ...item.node, comment: flatsInfo.value[item.node.pk] }));

    if (args.append) {
      container.value.push(...newItems);
    } else {
      container.value = newItems;
    }
    pageInfo.value = result.pageInfo;
  }

  function updateCommentInInfo (pk: number, comment: string) {
    if (pk in availableFlatsIds.value) {
      set(availableFlatsIds.value, pk, comment);
      const lot = currentAvailable.value.find((item) => String(item.pk) === String(pk));
      if (lot) {
        set(lot, 'comment', comment);
        currentAvailable.value = [...currentAvailable.value]; // жуткий костыль, только потому что с SSR первые 6 загруженных лотов прилетают без реактивности
      }
    } else if (pk in unavailableFlatsIds.value) {
      set(unavailableFlatsIds.value, pk, comment);
      const lot = currentUnavailable.value.find((item) => String(item.pk) === String(pk));
      if (lot) {
        set(lot, 'comment', comment);
        currentAvailable.value = [...currentAvailable.value]; // жуткий костыль, только потому что с SSR первые 6 загруженных лотов прилетают без реактивности
      }
    }
  }

  async function editComment (pk: number, comment: string) {
    await editedCollectionComment(pk, comment);
    updateCommentInInfo(pk, comment);
  }
  async function addComment (pk: number, comment: string) {
    await addedCollectionComment(pk, comment);
    updateCommentInInfo(pk, comment);
  }

  return {
    isInCollection,
    addItem,
    removeItem,
    removeItemByPk,
    loading,
    specs,
    loadedSpecs,
    facets,
    getSpecs,
    getFacets,
    currentAvailable,
    currentUnavailable,
    availablePageInfo,
    unavailablePageInfo,
    getCurrentByIds,
    flatsInfo,
    availableFlatsIds,
    unavailableFlatsIds,
    totalAvailableCount,
    loadCollectionItemsInfo,
    editComment,
    addComment,
  };
});
